import Income, {getIncomeTypeDef, IncomeType} from "../../../model/Income";
import {Box, FormControlLabel, IconButton, InputAdornment, Paper, Switch, Tooltip, Typography, useTheme} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {globalColors} from "../../../styles/globalStyles";
import * as React from "react";
import {useEffect, useState} from "react";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreateIncomeInput, UpdateIncomeInput} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";

import Model from "../../../model/Model";
import {isManagedRiderIncome} from "../../../components/calculator/AnnuityRider";
import Schedule, {Frequency} from "../../../model/Schedule";
import ScheduleSelector from "../../../components/controls/ScheduleSelector";
import FormGroupSpacer from "../../../components/form/FormGroupSpacer";
import MilestoneDateFieldValidator from "../../../components/form/MilestoneDateFieldValidator";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import {
  getISODateFromDate,
  moneyToNumberFormat,
  numberToMoneyFormat, numberToPercentFormat
} from "../../../stores/StoreUtilities";
import {isNumber} from "../../../model/ModelUtilities";
import Milestone from "../../../model/Milestone";
import {addYears} from "date-fns";
import Person from "../../../model/Person";
import Tracking from "../../../components/Tracking";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Snapshot from "../../../model/Snapshot";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import FormValidator from "../../../components/form/FormValidator";
import DialogButton from "../../../components/form/DialogButton";

const IncomeEditDialog = ({
  open,
  income,
  model,
  snapshot,
  onClose,
  onSave,
  onDelete,
  onArchive,
  quickForm
}: {
  open?: boolean
  income: Income
  model: Model
  snapshot?: Snapshot
  onClose?(): void
  onSave?(income: Income): void
  onDelete?(income: Income): void
  onArchive?(income: Income): void
  quickForm?: boolean
}) => {
  const [incomeModel, setIncomeModel] = useState<Income>(income)
  const [description, setDescription] = useState<string>("")
  const [amount, setAmount] = useState<string | undefined>()
  const [amountDate, setAmountDate] = useState<Date | undefined>()
  const [amountLock, setAmountLock] = useState<boolean>(false)
  const [incomeType, setIncomeType] = useState<IncomeType | undefined>()
  const [person, setPerson] = useState<Person | undefined>()
  const [owner, setOwner] = useState<string | undefined>()
  const [allowJoint, setAllowJoint] = useState<boolean>(true)
  const [schedule, setSchedule] = useState<Schedule | undefined>()
  const [start, setStart] = useState<string>()
  const [startLock, setStartLock] = useState<boolean>(false)
  const [end, setEnd] = useState<string>()
  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [annualInfLock, setAnnualInfLock] = useState<"Model" | "User" | undefined>()
  const [survivorPercent, setSurvivorPercent] = useState<string | undefined>()
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [archived, setArchived] = useState<boolean>(false)
  const [paycheck, setPaycheck] = useState<Income | undefined>()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [excludeFromNIIT, setExcludeFromNIIT] = useState<boolean>(false)

  const { modelStore, userStore, calculator, notify } = useResources()

  const theme = useTheme()

  // const styles = {
  //   actions: {
  //     display:"flex",
  //     flexDirection:"row",
  //     width:"100%",
  //     justifyContent:"space-between",
  //     alignItems:"center",
  //     marginTop: theme.spacing(1)
  //   },
  //   row: {
  //     maxHeight: 48
  //   },
  //   formLabel: {
  //     fontSize: 12,
  //     fontWeight: 400,
  //     color: theme.palette.grey["700"]
  //   },
  //   stateField: {
  //     top: 5,
  //     width: "100%"
  //   }
  // }

  useEffect(() => {
    const typedef = getIncomeTypeDef(income.incomeType ?? IncomeType.None)
    setIncomeModel(income)
    setDescription(income.description)
    setAmount(numberToMoneyFormat(income.amount, 0))
    setAmountDate(income.amountDate)
    setIncomeType(income.incomeType)
    const person = income.ownerId ? model.getPerson(income.ownerId) : model.getDefaultPerson(typedef.allowJoint)
    setPerson(person)
    setOwner(person ? person.nickname : model.getDefaultPersonNickname(typedef.allowJoint))
    setAllowJoint(typedef.allowJoint)
    setSchedule(income.schedule ?? new Schedule({name: "Monthly", frequency: Frequency.Monthly, interval: 1}))
    setStart(income.start)
    setEnd(income.end)
    setAnnualInf(isNumber(income.annualInf) ? numberToPercentFormat(income.annualInf, 2) : "")
    if (income.infLock) {
      setAnnualInfLock("User")
    } else if (model.hasLock(income.id, "annualInf")) {
      setAnnualInfLock("Model")
    }
    setSurvivorPercent(isNumber(income.survivorPercent) ? numberToPercentFormat(income.survivorPercent / 100, 2) : undefined)
    setAmountLock(model.hasLock(income.id, "amount"))
    setStartLock(model.hasLock(income.id, "start"))
    setIsLocked(model.hasLock(income.id, "*") || model.isLocked())
    setArchived(income.archived)
    setExcludeFromNIIT(income.excludeFromNIIT === true)
    if (income.paycheckId) {
      setPaycheck(model.getIncomeById(income.paycheckId))
      setAmountLock(true)
    }
    setIsOpen(open === true)
  }, [income, open])

  useEffect(() => {
    if (person) {
      setMilestones(getMilestones(person))
    }
  }, [person])

  useEffect(() => {
    const typedef = getIncomeTypeDef(incomeType ?? IncomeType.None)
    setAllowJoint(typedef.allowJoint)
  }, [incomeType])

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    try {
      if (isManagedRiderIncome(model, incomeModel)) {
        // Rider income is managed by its linked annuity asset — editing it here would desync the two
        notify.show('info', "This income is managed by its linked asset. Edit it from the Assets & Liabilities page.")
        return
      }
      const person = model.getPersonByNickname(owner)
      let updatedIncome: Income | undefined
      const amountValue = moneyToNumberFormat(amount, 0) ?? 0

      if (!incomeModel.createdAt) {
        const input: CreateIncomeInput = {
          id: incomeModel.id,
          accountId: incomeModel.accountId,
          userId: incomeModel.userId,
          modelId: incomeModel.modelId,
          description: description,
          amount: amountValue,
          amountDate: amountDate ? getISODateFromDate(amountDate) : null,
          incomeType: incomeType,
          schedule: JSON.stringify(schedule),
          start: start,
          end: end ?? null,
          annualInf: annualInf !== undefined ? parseFloat(annualInf) / 100.0 : null,
          infLock: annualInfLock === "User",
          survivorPercent: null,
          ownerId: person ? person.id : null,
          sortOrder: income.sortOrder
        }
        if (incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable || incomeType === IncomeType.PensionsAndAnnuitiesTaxable) {
          input.survivorPercent = survivorPercent !== undefined ? parseInt(survivorPercent) : null
        }
        if (incomeType === IncomeType.OtherIncome) {
          input.excludeFromNIIT = excludeFromNIIT
        }

        updatedIncome = await modelStore.createIncome(input)
        Tracking.event({action: "Income Created"})
      } else {
        const input: UpdateIncomeInput = {
          id: incomeModel.id,
          accountId: incomeModel.accountId,
          userId: incomeModel.userId,
          modelId: incomeModel.modelId,
          description: description,
          amount: amountValue,
          amountDate: snapshot ? getISODateFromDate(snapshot.date) : null,
          incomeType: incomeType,
          schedule: JSON.stringify(schedule),
          start: start,
          end: end ?? null,
          annualInf: annualInf !== undefined ? parseFloat(annualInf) / 100.0 : null,
          infLock: annualInfLock === "User",
          survivorPercent: null,
          ownerId: person ? person.id : null,
          sortOrder: income.sortOrder
        }
        if (amountLock) {
          delete input.amount
          delete input.amountDate
        }
        if (startLock) {
          delete input.start
        }
        if (annualInfLock === "Model") {
          delete input.annualInf
        }

        if (incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable || incomeType === IncomeType.PensionsAndAnnuitiesTaxable) {
          input.survivorPercent = survivorPercent !== undefined ? parseInt(survivorPercent) : null
        }
        if (incomeType === IncomeType.OtherIncome) {
          input.excludeFromNIIT = excludeFromNIIT
        }

        updatedIncome = await modelStore.updateIncome(input)
        Tracking.event({action: "Income Updated"})
      }

      if (onSave && updatedIncome) {
        onSave(updatedIncome)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const handleArchive = async () => {
    if (isManagedRiderIncome(model, incomeModel)) {
      notify.show('info', "This income is managed by its linked asset. Archive the asset from the Assets & Liabilities page.")
      return
    }
    if (onArchive) {
      if (incomeModel.createdAt) {
        const income = await modelStore.updateIncome({id: incomeModel.id, archived: !incomeModel.archived})
        if (income) {
          onArchive(income)
        }
      } else if (onDelete) {
        onDelete(incomeModel)
      }
    }
  }

  const handleDelete = async () => {
    if (isManagedRiderIncome(model, incomeModel)) {
      notify.show('info', "This income is managed by its linked asset. Delete the asset from the Assets & Liabilities page.")
      return
    }
    if (onDelete) {
      if (incomeModel.createdAt) {
        const deleted = await modelStore.deleteIncome(incomeModel.id)
        if (deleted) {
          onDelete(deleted)
        }
      } else {
        onDelete(incomeModel)
      }
    }
  }

  const getMilestones = (person: Person): Milestone[] => {
    let options: Milestone[] = []
    const timeline = calculator.personTimeline(person)
    if (timeline) {
      const ages = [62, 63, 64, 65, 66, 67, 68, 69, 70]
      options = ages.map((age: number) => {
        let label
        if (age === timeline.earlySocialSecurityAge) {
          label = `${age} (Early Social Security)`
        } else if (age === timeline.fullSocialSecurityAge) {
          label = `${age} (Full Social Security)`
        } else if (age === timeline.delayedSocialSecurityAge) {
          label = `${age} (Delayed Social Security)`
        } else {
          label = String(age)
        }
        const date = addYears(timeline.birthDate, age)
        return new Milestone({
          id: getISODateFromDate(date),
          label: label,
          date: date
        })
      })
    }
    return options
  }

  const handleInfLock = (typename: string) => {
    if (annualInfLock === "User") {
      if (model.getOverrideInflationLock(typename)) {
        setAnnualInfLock("Model")
        setAnnualInf(numberToPercentFormat(model.getDefaultInflationRate(typename)))
      } else {
        setAnnualInfLock(undefined)
      }
    } else if (annualInfLock === "Model") {
      setAnnualInfLock("User")
    } else if (annualInfLock === undefined) {
      setAnnualInfLock("User")
    }
  }

  const getEndHelperText = (incomeType?: IncomeType) => {
    let helperText = ""
    if (incomeType !== undefined) {
      const incomeTypeDef = getIncomeTypeDef(incomeType)
      helperText = (incomeTypeDef.defaultEndDate === 'retireDate') ?
        "Default: Owner's Retirement" : "Default: Owner's Life Exp"
    }
    return helperText
  }

  const renderAnnualInfField = (typename: string) => {
    let endAdornment
    let helperText
    let disabled = false
    if (userStore.isFree) {
      helperText = `Default: ${Model.defaultInflationRate}`
    } else if (annualInfLock === "User") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
            disabled={isLocked}
          >
            <LockPersonIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by User"
    } else if (annualInfLock === "Model") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
            disabled={isLocked}
          >
            <LockIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by Inflation Strategy"
      disabled = true
    } else {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle inflation lock"
            onClick={() => handleInfLock(typename)}
            edge="end"
            disabled={isLocked}
          >
            <LockOpenIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = `Default: ${numberToPercentFormat(model.getDefaultInflationRate(typename), 2)}`
    }

    return (
      <TextFieldValidator
        type="string"
        validators={{ required: false, minValue: 0 }}
        name="annualInf"
        variant="standard"
        margin="dense"
        fullWidth
        label="Annual Inflation %"
        value={annualInf}
        InputProps={{
          endAdornment: endAdornment
        }}
        helperText={helperText}
        disabled={isLocked || disabled}
        onChange={(event: any) => setAnnualInf(event.target.value)}
      />
    )
  }

  const renderLock = () => {
    if (isLocked || Boolean(paycheck)) {
      let lockMsg = ""
      if (model.isLocked()) {
        lockMsg = model.getLock(model.id, "*") ?? ""
      } else if (paycheck) {
        lockMsg = `Locked by ${paycheck.description}`
      } else if (model.hasLock(incomeModel.id, "*")) {
        lockMsg = `Locked by Social Security Strategy`
      }
      return (<Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
        <LockIcon sx={{color: theme.palette.primary.main, mr:1}}/>
        <Typography variant="body2" color={theme.palette.primary.main}>
          {lockMsg}
        </Typography>
      </Box>)
    } else {
      return null
    }
  }

  if (!incomeModel || !isOpen) {
    return null
  }

  if (quickForm) {
    return (
      <FormValidator id="income-quick-form" onSubmit={handleSave}>
        <Paper elevation={1} sx={{m:0, mb:2, px:2, py:1}}>
          <TextFieldValidator
            margin="normal"
            name="description"
            label="Title"
            type="text"
            fullWidth
            variant="standard"
            required
            validators={{ required: true }}
            value={description}
            disabled={isLocked}
            onChange={(event: any) => setDescription(event.target.value)}
          />
          <Box display="flex" flexGrow={1} width="100%" justifyContent="stretch">
            <TextFieldValidator
              type="string"
              validators={{ isMoney: true }}
              name="amount"
              variant="standard"
              margin="dense"
              fullWidth
              sx={{width:"50%"}}
              label="Amount"
              value={amount}
              locked={isLocked || amountLock}
              helperText={amountLock ? "Locked by Social Security Strategy" : undefined}
              onChange={(event: any) => setAmount(event.target.value)}
            />
            <FormGroupSpacer/>
            <TextFieldValidator
              type="string"
              validators={{ required: false, minValue: 0 }}
              name="annualInf"
              variant="standard"
              margin="dense"
              fullWidth
              sx={{width:"50%"}}
              label="Annual Inflation %"
              helperText={`Default: 0%`}
              value={annualInf}
              onChange={(event: any) => setAnnualInf(event.target.value)}
            />
          </Box>
          <ScheduleSelector
            value={schedule!}
            disabled={isLocked || Boolean(paycheck)}
            startDate={model.getDate(start)}
            onChange={(value: Schedule) => setSchedule(value)}
          />
          <TextFieldValidator
            type="text"
            validators={{ required: true }}
            required
            name="owner"
            label="Owner"
            variant="standard"
            autocompleteOptions={{
              freeSolo: false,
              options: model.getPersonNicknames(allowJoint),
              value: owner,
              disabled: (isLocked || Boolean(paycheck)),
              onChange: (event: any, value: string, reason: any) => {
                setOwner(value)
                setPerson(model.getPersonByNickname(value))
              }
            }}
          />
          <TextFieldValidator
            type="text"
            validators={{ required: true }}
            required
            name="incomeType"
            label="Income Type"
            variant="standard"
            autocompleteOptions={{
              freeSolo: false,
              options: Object.values(IncomeType).filter((v) => !isNaN(Number(v))),
              getOptionLabel: (option: number) => {
                const incomeTypeDef = getIncomeTypeDef(option)
                return(incomeTypeDef.label)
              },
              value: incomeType,
              disabled: isLocked || Boolean(paycheck),
              onChange: (event: any, value: number, reason: any) => {
                setIncomeType(value)
              }
            }}
          />
          {(incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable || incomeType === IncomeType.PensionsAndAnnuitiesTaxable) &&
            <TextFieldValidator
              type="string"
              validators={{ required: false, minValue: 0 }}
              name="survivorPercent"
              variant="standard"
              margin="dense"
              fullWidth
              label="Survivor %"
              value={survivorPercent}
              disabled={isLocked}
              onChange={(event: any) => setSurvivorPercent(event.target.value)}
            />
          }
          {incomeType === IncomeType.OtherIncome &&
            <FormControlLabel control={
              <Switch checked={excludeFromNIIT} disabled={isLocked}
                      onChange={(event: any) => setExcludeFromNIIT(event.target.checked)}
              />}
              label={<Box display="flex" alignItems="center" sx={{fontSize: '0.9rem'}}>Exclude from NIIT
                <Tooltip title={<Typography variant="body2">Net Investment Income Tax (NIIT) is a 3.8% surtax on investment income when it exceeds $200K (single) or $250K (married filing jointly).</Typography>}>
                  <InfoIcon sx={{ml: 0.5, fontSize: '1rem', color: globalColors.primary}}/>
                </Tooltip>
              </Box>}
              sx={{pt:1}}
            />
          }
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            {incomeType === IncomeType.SocialSecurityBenefits && owner &&
              <MilestoneDateFieldValidator
                name="start"
                label="Start Date"
                milestones={milestones}
                value={start}
                width="50%"
                required={incomeType === IncomeType.SocialSecurityBenefits}
                disabled={isLocked || startLock || Boolean(paycheck)}
                helperText={startLock ? "Locked by Social Security Strategy" : undefined}
                onChange={(value: string) => {
                  setStart(value)
                }}
              />
            }
            {(incomeType !== IncomeType.SocialSecurityBenefits || !owner) &&
              <MilestoneDateFieldValidator
                name="start"
                label="Start Date"
                model={model}
                value={start}
                width="50%"
                required={incomeType !== IncomeType.SocialSecurityBenefits}
                disabled={isLocked || startLock || Boolean(paycheck)}
                helperText={startLock ? "Locked by Social Security Strategy" : undefined}
                onChange={(value: string) => setStart(value)}
              />
            }
            <FormGroupSpacer/>
            <MilestoneDateFieldValidator
              name="end"
              label="End Date"
              model={model}
              value={end}
              disabled={isLocked || Boolean(paycheck)}
              width="50%"
              helperText={getEndHelperText(incomeType)}
              onChange={(value: string) => setEnd(value)}
            />
          </Box>
        </Paper>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <DialogButton variant="primary" type="submit" spacing="right" data-testid="income-quick-save">
              Save
            </DialogButton>
            <DialogButton variant="secondary" type="button" onClick={handleClose} spacing="right" data-testid="income-quick-cancel">
              Cancel
            </DialogButton>
          </Box>
        </Box>
      </FormValidator>
    )
  }

  return (
    <ModelEditDialog title="Edit Income" open={isOpen}
                     onCancel={handleClose}
                     onSave={!isLocked ? handleSave : undefined}
                     onDelete={!isLocked ? handleDelete : undefined}
                     archived={archived}
                     onArchive={!isLocked && onArchive ? handleArchive : undefined}
    >
      {renderLock()}
      <TextFieldValidator
        margin="normal"
        name="description"
        label="Title"
        type="text"
        fullWidth
        variant="standard"
        required
        validators={{ required: true }}
        value={description}
        disabled={isLocked}
        onChange={(event: any) => setDescription(event.target.value)}
      />
      <Box display="flex" flexDirection="row" justifyContent="stretch">
        <TextFieldValidator
          type="string"
          validators={{ isMoney: true }}
          name="amount"
          variant="standard"
          margin="dense"
          sx={{width:"50%"}}
          label="Amount"
          value={amount}
          locked={isLocked || amountLock}
          helperText={amountLock ? "Locked by Social Security Strategy" : undefined}
          onChange={(event: any) => setAmount(event.target.value)}
        />
        <FormGroupSpacer/>
        <Box pt={1} width="50%">
          <DatePickerValidator
            name="amountDate"
            label="Amount Date"
            value={amountDate}
            format="MM-dd-yyyy"
            variant="standard"
            margin="dense"
            fullWidth
            required
            disabled
            onChange={(newValue: any) => {
              setAmountDate(newValue)
            }}
          />
        </Box>
      </Box>
      <ScheduleSelector
        value={schedule!}
        disabled={isLocked || Boolean(paycheck)}
        startDate={model.getDate(start)}
        onChange={(value: Schedule) => setSchedule(value)}
      />
      <TextFieldValidator
        type="text"
        validators={{ required: true }}
        required
        name="owner"
        label="Owner"
        variant="standard"
        autocompleteOptions={{
          freeSolo: false,
          options: model.getPersonNicknames(allowJoint),
          value: owner,
          disabled: (isLocked || Boolean(paycheck)),
          onChange: (event: any, value: string, reason: any) => {
            setOwner(value)
            setPerson(model.getPersonByNickname(value))
          }
        }}
      />
      <TextFieldValidator
        type="text"
        validators={{ required: true }}
        required
        name="incomeType"
        label="Income Type"
        variant="standard"
        autocompleteOptions={{
          freeSolo: false,
          options: Object.values(IncomeType).filter((v) => !isNaN(Number(v))),
          getOptionLabel: (option: number) => {
            const incomeTypeDef = getIncomeTypeDef(option)
            return(incomeTypeDef.label)
          },
          value: incomeType,
          disabled: isLocked || Boolean(paycheck),
          onChange: (event: any, value: number, reason: any) => {
            setIncomeType(value)
          }
        }}
      />
      {(incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable || incomeType === IncomeType.PensionsAndAnnuitiesTaxable) &&
        <TextFieldValidator
          type="string"
          validators={{ required: false, minValue: 0 }}
          name="survivorPercent"
          variant="standard"
          margin="dense"
          fullWidth
          label="Survivor %"
          value={survivorPercent}
          disabled={isLocked}
          onChange={(event: any) => setSurvivorPercent(event.target.value)}
        />
      }
      {incomeType === IncomeType.OtherIncome &&
        <FormControlLabel control={
          <Switch checked={excludeFromNIIT} disabled={isLocked}
                  onChange={(event: any) => setExcludeFromNIIT(event.target.checked)}
          />}
          label={<Box display="flex" alignItems="center" sx={{fontSize: '0.9rem'}}>Exclude from NIIT
            <Tooltip title={<Typography variant="body2">Net Investment Income Tax (NIIT) is a 3.8% surtax on investment income when it exceeds $200K (single) or $250K (married filing jointly).</Typography>}>
              <InfoIcon sx={{ml: 0.5, fontSize: '1rem', color: globalColors.primary}}/>
            </Tooltip>
          </Box>}
          sx={{pt:1}}
        />
      }
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        {incomeType === IncomeType.SocialSecurityBenefits && owner &&
          <MilestoneDateFieldValidator
            name="start"
            label="Start Date"
            milestones={milestones}
            value={start}
            width="50%"
            required={incomeType === IncomeType.SocialSecurityBenefits}
            disabled={isLocked || startLock || Boolean(paycheck)}
            helperText={startLock ? "Locked by Social Security Strategy" : undefined}
            onChange={(value: string) => {
              setStart(value)
            }}
          />
        }
        {(incomeType !== IncomeType.SocialSecurityBenefits || !owner) &&
          <MilestoneDateFieldValidator
            name="start"
            label="Start Date"
            model={model}
            value={start}
            width="50%"
            required={incomeType !== IncomeType.SocialSecurityBenefits}
            disabled={isLocked || startLock || Boolean(paycheck)}
            helperText={startLock ? "Locked by Social Security Strategy" : undefined}
            onChange={(value: string) => setStart(value)}
          />
        }
        <FormGroupSpacer/>
        <MilestoneDateFieldValidator
          name="end"
          label="End Date"
          model={model}
          value={end}
          disabled={isLocked || Boolean(paycheck)}
          width="50%"
          helperText={getEndHelperText(incomeType)}
          onChange={(value: string) => setEnd(value)}
        />
      </Box>
      {renderAnnualInfField("income")}
    </ModelEditDialog>
  )
}

export default IncomeEditDialog